import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Mail, Phone, MapPin, Package, LogOut, ArrowLeft, Edit2, Save, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { updateMe, getMyOrders } from '../services/api';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logoutUser, updateUser, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    city: '',
    postal_code: ''
  });

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/prijava');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        phone: user.phone || '',
        address: user.address || '',
        city: user.city || '',
        postal_code: user.postal_code || ''
      });
    }
  }, [user]);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!isAuthenticated) return;
      try {
        const data = await getMyOrders();
        setOrders(data);
      } catch (err) {
        console.error('Error fetching orders:', err);
      } finally {
        setLoadingOrders(false);
      }
    };

    fetchOrders();
  }, [isAuthenticated]);

  const handleLogout = () => {
    logoutUser();
    navigate('/');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateMe(formData);
      updateUser(updated);
      setEditing(false);
    } catch (err) {
      console.error('Error updating profile:', err);
    } finally {
      setSaving(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Na čekanju',
      confirmed: 'Potvrđeno',
      shipped: 'Poslato',
      delivered: 'Dostavljeno',
      cancelled: 'Otkazano'
    };
    return labels[status] || status;
  };

  const getStatusClass = (status) => {
    const classes = {
      pending: 'status-pending',
      confirmed: 'status-confirmed',
      shipped: 'status-shipped',
      delivered: 'status-delivered',
      cancelled: 'status-cancelled'
    };
    return classes[status] || '';
  };

  if (authLoading) {
    return <div className="profile-page"><div className="loading-container">Učitavanje...</div></div>;
  }

  if (!user) return null;

  return (
    <div className="profile-page">
      <div className="profile-container">
        <Link to="/" className="profile-back">
          <ArrowLeft size={18} />
          Nazad na početnu
        </Link>

        <div className="profile-header">
          <div className="profile-avatar">
            <User size={40} />
          </div>
          <div className="profile-info">
            <h1>{user.name}</h1>
            <p>{user.email}</p>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            <LogOut size={18} />
            Odjavi se
          </button>
        </div>

        <div className="profile-section">
          <div className="section-header">
            <h2>Lični podaci</h2>
            {!editing ? (
              <button className="edit-btn" onClick={() => setEditing(true)}>
                <Edit2 size={16} />
                Izmeni
              </button>
            ) : (
              <div className="edit-actions">
                <button className="save-btn" onClick={handleSave} disabled={saving}>
                  <Save size={16} />
                  {saving ? 'Čuvanje...' : 'Sačuvaj'}
                </button>
                <button className="cancel-btn" onClick={() => setEditing(false)}>
                  <X size={16} />
                </button>
              </div>
            )}
          </div>

          <div className="profile-details">
            <div className="detail-item">
              <Mail size={18} />
              <div>
                <label>Email</label>
                <p>{user.email}</p>
              </div>
            </div>

            <div className="detail-item">
              <User size={18} />
              <div>
                <label>Ime</label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                ) : (
                  <p>{user.name}</p>
                )}
              </div>
            </div>

            <div className="detail-item">
              <Phone size={18} />
              <div>
                <label>Telefon</label>
                {editing ? (
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Unesite telefon"
                  />
                ) : (
                  <p>{user.phone || 'Nije uneto'}</p>
                )}
              </div>
            </div>

            <div className="detail-item">
              <MapPin size={18} />
              <div>
                <label>Adresa</label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Unesite adresu"
                  />
                ) : (
                  <p>{user.address || 'Nije uneto'}</p>
                )}
              </div>
            </div>

            <div className="detail-row">
              <div className="detail-item">
                <div>
                  <label>Grad</label>
                  {editing ? (
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="Grad"
                    />
                  ) : (
                    <p>{user.city || 'Nije uneto'}</p>
                  )}
                </div>
              </div>
              <div className="detail-item">
                <div>
                  <label>Poštanski broj</label>
                  {editing ? (
                    <input
                      type="text"
                      value={formData.postal_code}
                      onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                      placeholder="21000"
                    />
                  ) : (
                    <p>{user.postal_code || 'Nije uneto'}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="profile-section">
          <h2>
            <Package size={20} />
            Moje narudžbine
          </h2>

          {loadingOrders ? (
            <p className="loading-text">Učitavanje narudžbina...</p>
          ) : orders.length === 0 ? (
            <div className="no-orders">
              <p>Nemate nijednu narudžbinu.</p>
              <Link to="/#products" className="shop-link">Pogledajte proizvode</Link>
            </div>
          ) : (
            <div className="orders-list">
              {orders.map((order) => (
                <div key={order.id} className="order-card">
                  <div className="order-header">
                    <span className="order-id">#{order.id.slice(0, 8)}</span>
                    <span className={`order-status ${getStatusClass(order.status)}`}>
                      {getStatusLabel(order.status)}
                    </span>
                  </div>
                  <div className="order-items">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="order-item">
                        <span>{item.name} ({item.size})</span>
                        <span>x{item.quantity}</span>
                      </div>
                    ))}
                  </div>
                  <div className="order-footer">
                    <span className="order-date">
                      {new Date(order.created_at).toLocaleDateString('sr-RS')}
                    </span>
                    <span className="order-total">{order.total.toLocaleString('sr-RS')} RSD</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
